import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LayoutTemplate, Play, Trash2, Save, Loader2 } from "lucide-react";
import api from "@/lib/api";

export function SaveTemplateButton({ run, onSaved }) {
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const save = async () => {
    try { const r = await api.post("/templates", { run_id: run.run_id, name: name || run.objective.slice(0, 40) }); toast.success(`Template “${r.data.name}” saved`); setOpen(false); setName(""); onSaved?.(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save template"); }
  };
  if (!open) return <button data-testid="save-template-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 hover:border-purple-500/40"><Save size={12} /> Save as template</button>;
  return (
    <div className="flex items-center gap-2" data-testid="save-template-form">
      <input data-testid="template-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Template name" className="bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none" />
      <button data-testid="template-save-confirm" onClick={save} className="text-xs px-3 py-1.5 rounded-full bg-purple-600 text-white">Save</button>
      <button onClick={() => setOpen(false)} className="text-xs text-slate-400">Cancel</button>
    </div>
  );
}

export function TemplatesPanel({ wsId, onRunStarted, refreshKey }) {
  const [tpls, setTpls] = useState([]);
  const [product, setProduct] = useState({});
  const [busy, setBusy] = useState("");
  const load = () => api.get("/templates").then((r) => setTpls(r.data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);

  const run = async (t) => {
    const p = (product[t.template_id] || "").trim();
    if (p.length < 2) return toast.error("Type the new product or brand focus first");
    setBusy(t.template_id);
    try { const r = await api.post(`/templates/${t.template_id}/run`, { product: p, workspace_id: wsId || null }); toast.success("Template run started"); onRunStarted?.(r.data); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not run template"); }
    setBusy("");
  };
  const remove = async (t) => { await api.delete(`/templates/${t.template_id}`); toast.message("Template deleted"); load(); };

  return (
    <div className="glass rounded-2xl p-5" data-testid="templates-panel">
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-3 inline-flex items-center gap-2"><LayoutTemplate size={13} /> Campaign templates · {tpls.length}</div>
      {tpls.length === 0 && <div data-testid="templates-empty" className="text-sm text-slate-500">Finish a run, then “Save as template” to re-run the same campaign for a new product in one click.</div>}
      <div className="space-y-2">
        {tpls.map((t) => (
          <div key={t.template_id} data-testid="template-item" className="rounded-xl border border-white/10 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <div className="text-sm font-medium flex-1 truncate">{t.name}</div>
              <span className="text-[10px] font-mono text-slate-500">{t.asset_count} assets · {(t.steps || []).join(" → ")} · ran {t.runs}×</span>
              <button data-testid="template-delete-btn" onClick={() => remove(t)} className="text-slate-500 hover:text-red-300"><Trash2 size={13} /></button>
            </div>
            <p className="text-[11px] text-slate-500 line-clamp-2">{t.objective}</p>
            <div className="flex gap-2">
              <input data-testid="template-product-input" value={product[t.template_id] || ""} onChange={(e) => setProduct({ ...product, [t.template_id]: e.target.value })} placeholder="New product / brand focus, e.g. Aranya Cold Brew cans" className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none" />
              <button data-testid="template-run-btn" disabled={busy === t.template_id} onClick={() => run(t)} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-600 to-cyan-600 text-white disabled:opacity-50">
                {busy === t.template_id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Run for this
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
